import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { AgGridReact } from '@ag-grid-community/react';
import {
	Wrapper,
	ButtonWrapper,
	SavingDiv,
	TableWrapper,
} from './VendorMapping.styles';
import {
	ApButtonSecondary,
	ApIcon,
	ApLoaderDefault,
	ApLoaderText,
} from '@alixpartners/ui-components';
import { Spinner } from 'components/LoadingIndicator/styles';
import createDatasource from './ServerSideDatasource';
import { updateVendorMappingEditData } from 'utils/apiRequests';
import { LoaderWrapper } from 'Data/Data.styles';

const VendorMapping = () => {
	const [gridApi, setGridApi] = useState();
	const [undos, setUndos] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [editedRows, setEditedRows] = useState([]);

	const gridStyle = useMemo(
		() => ({
			height: '70vh',
			width: '100%',
		}),
		[]
	);

	const defaultColDefs = useMemo(() => {
		return {
			flex: 1,
			minWidth: 100,
			editable: true,
		};
	}, []);
	const gridRef = useRef();

	const [columnDefs] = useState([
		{
			headerName: 'Raw Vendor Name',
			field: 'VENDOR_NAME',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'VENDOR_NAME',
			editable: false,
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Harmonized Vendor Name',
			field: 'HARMONIZED_VENDOR_NAME',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'HARMONIZED_VENDOR_NAME',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Updated?',
			field: 'IS_REVIEWED',
			filterParams: {
				filterOptions: ['equals'],
			},
			editable: false,
			colId: 'IS_REVIEWED',
			filter: 'agTextColumnFilter',
		},
	]);

	const undo = shouldNotSave => {
		const lastActionData = undos.slice(-1)[0];
		const remainingUndos = undos.slice(0, -1);

		setUndos(remainingUndos);
		setDataToRows(lastActionData);
	};

	const setDataToRows = data => {
		if (gridApi) {
			data.forEach(rowData => {
				const rowNode = gridApi.getRowNode(rowData.ID);
				const flashColumns = [];

				Object.keys(rowNode.data).forEach(key => {
					if (rowNode.data[key] !== rowData[key]) {
						flashColumns.push(key);
					}
				});

				gridApi.flashCells({
					rowNodes: [rowNode],
					columns: flashColumns,
				});
				rowNode.setData(rowData);
			});
		}
	};

	const updateObjectByVendorName = (vendorName, newObject) => {
		setEditedRows(prevState => {
			const existingIndex = prevState.findIndex(
				obj => obj.VENDOR_NAME === vendorName
			);

			if (existingIndex !== -1) {
				const updatedState = [...prevState];
				updatedState[existingIndex] = newObject;
				return updatedState;
			} else {
				return [...prevState, newObject];
			}
		});
	};

	const handleCellValueChange = event => {
		const selectedRowNodes = gridApi.getSelectedNodes();
		if (
			event.oldValue &&
			event.newValue &&
			event.oldValue.toString() === event.newValue.toString() &&
			selectedRowNodes.length === 1
		) {
			return;
		}
		const modifiedRows = [];
		modifiedRows.push(event.data);
		updateObjectByVendorName(event.data.VENDOR_NAME, event.data);
	};

	const [gridOptions] = useState({
		modules: AllModules,
		columnDefs: columnDefs,
		cacheBlockSize: 500,
		maxBlocksInCache: 10,
		enableColResize: true,
		enableSorting: true,
		enableFilter: true,
		rowModelType: 'serverSide',
		floatingFilter: true,
		maxConcurrentDatasourceRequests: 1,
		multiSortKey: 'ctrl',
		enableRangeSelection: true,
		suppressCutToClipboard: true,
	});

	const onGridReady = useCallback(
		params => {
			setGridApi(params.api);

			gridRef.current.api.startEditingCell(params);
			const gridOptionsnew = {
				columnDefs,

				suppressRowClickSelection: true,
				...gridOptions,
			};
			const datasource = createDatasource(
				gridOptionsnew,
				params.columnApi,
				params.api
			);
			params.api.setServerSideDatasource(datasource);
		},
		[columnDefs, gridOptions]
	);

	const updateVendorNormilization = () => {
		const groupedData = editedRows.reduce((acc, item) => {
			if (!acc[item.HARMONIZED_VENDOR_NAME]) {
				acc[item.HARMONIZED_VENDOR_NAME] = [];
			}
			acc[item.HARMONIZED_VENDOR_NAME].push(item.VENDOR_NAME);
			return acc;
		}, {});

		// Transforming grouped data into the desired format
		const updates = {
			updates: Object.keys(groupedData).map(key => ({
				key,
				values: groupedData[key],
			})),
		};

		updateVendorMappingEditData(updates)
			.then(() => {
				setIsSaving(state => !state);
			})
			.catch(error => {
				undo(true);
				setIsSaving(state => !state);
			});
		setIsSaving(state => !state);
	};

	return (
		<>
			{isSaving ? (
				<SavingDiv>
					<LoaderWrapper>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderWrapper>
				</SavingDiv>
			) : (
				<Wrapper>
					<TableWrapper>
						<div style={gridStyle} className="ag-theme-balham">
							<AgGridReact
								ref={gridRef}
								modules={AllModules}
								columnDefs={columnDefs}
								cacheBlockSize={500}
								defaultColDef={defaultColDefs}
								maxBlocksInCache={10}
								enableColResize={true}
								enableSorting={true}
								enableFilter={true}
								enableGroupEdit={true}
								rowModelType="serverSide"
								maxConcurrentDatasourceRequests={1}
								multiSortKey="ctrl"
								onCellValueChanged={handleCellValueChange}
								onGridReady={onGridReady}
								enableRangeSelection={true}
								suppressCutToClipboard={true}
							/>
						</div>
					</TableWrapper>
					<div style={{ width: '100%' }}>
						<ButtonWrapper>
							<ApButtonSecondary
								style={{ marginRight: '10px' }}
								onClick={() => updateVendorNormilization()}
								disabled={editedRows.length === 0}
							>
								{isSaving ? (
									<Spinner size={24} thickness={3} />
								) : (
									<ApIcon iconName="save" material={true} iconColor="black" />
								)}
								&nbsp; Save
							</ApButtonSecondary>
						</ButtonWrapper>
					</div>
				</Wrapper>
			)}
		</>
	);
};

export default VendorMapping;
